import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DisplayForm from '../../components/common/DisplayForm';
import QRCodeDisplay from '../../components/common/QRCodeDisplay';
import DisplaySessionsList from '../../components/display/DisplaySessionsList';
import { useDisplayNotifications } from '../../contexts/DisplayNotificationContext';
import { 
  DisplayData, 
  fetchDisplay, 
  updateDisplayConfig, 
  activateDisplay, 
  deactivateDisplay 
} from '../../services/displayService';

interface DisplayDetailParams {
  [key: string]: string | undefined;
  id?: string;
}

const DisplayDetailPage: React.FC = () => {
  const { id } = useParams<DisplayDetailParams>();
  const navigate = useNavigate();
  const { addNotification } = useDisplayNotifications();
  
  const [display, setDisplay] = useState<DisplayData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showActivateConfirm, setShowActivateConfirm] = useState<boolean>(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState<boolean>(false);

  // Fetch display details on mount
  useEffect(() => {
    if (!id) {
      setError('Display ID is required');
      setLoading(false);
      return;
    }

    const loadDisplay = async () => {
      try {
        setLoading(true);
        setError(null);
        const displayData = await fetchDisplay(id);
        setDisplay(displayData);
      } catch (err) {
        console.error('Error loading display:', err);
        setError(err instanceof Error ? err.message : 'Failed to load display details');
      } finally {
        setLoading(false);
      }
    };

    loadDisplay();
  }, [id]);

  // Handle form submission for updating display
  const handleUpdateDisplay = async (formData: any) => {
    if (!id || !display) return;

    try {
      setError(null);
      
      // Prepare update data with safe navigation
      const updateData = {
        name: formData.name,
        customName: formData.customName,
        location: {
          zipcode: formData.location?.zipcode || '',
          label: formData.location?.label || ''
        }
      };
      
      // Update display configuration
      const updated = await updateDisplayConfig(id, updateData);
      
      // Update local state
      setDisplay(updated);
      setActionMessage({ type: 'success', text: 'Display updated successfully' });
      setIsEditing(false);
      
      // Add notification
      addNotification({
        type: 'success',
        message: `Display "${updated.name}" updated successfully`,
        displayId: updated._id,
        displayName: updated.name
      });
      
      // Clear message after 3 seconds
      setTimeout(() => setActionMessage(null), 3000);
    } catch (err) {
      console.error('Error updating display:', err);
      setError(err instanceof Error ? err.message : 'Failed to update display');
      
      // Add error notification
      addNotification({
        type: 'error',
        message: `Failed to update display "${display.name}"`,
        displayId: display._id,
        displayName: display.name
      });
    }
  };

  // Handle display activation
  const handleActivateDisplay = async () => {
    if (!id || !display) return;
    
    try {
      setError(null);
      setShowActivateConfirm(false);
      
      // Activate the display
      const result = await activateDisplay(id);
      
      // Update local state
      setDisplay(result);
      setActionMessage({ type: 'success', text: 'Display activated successfully' });
      
      // Add notification
      addNotification({
        type: 'success',
        message: `Display "${result.name}" activated successfully`,
        displayId: result._id,
        displayName: result.name
      });
      
      // Clear message after 3 seconds
      setTimeout(() => setActionMessage(null), 3000);
    } catch (err) {
      console.error('Error activating display:', err);
      setError(err instanceof Error ? err.message : 'Failed to activate display');
      
      // Add error notification
      addNotification({
        type: 'error',
        message: `Failed to activate display "${display.name}"`,
        displayId: display._id,
        displayName: display.name
      });
    }
  };

  // Handle display deactivation
  const handleDeactivateDisplay = async () => {
    if (!id || !display) return;
    
    try {
      setError(null);
      setShowDeactivateConfirm(false);
      
      // Deactivate the display
      const result = await deactivateDisplay(id);
      
      // Update local state
      setDisplay(result);
      setActionMessage({ type: 'success', text: 'Display deactivated successfully' });
      
      // Add notification
      addNotification({
        type: 'warning',
        message: `Display "${result.name}" deactivated`,
        displayId: result._id,
        displayName: result.name
      });
      
      // Clear message after 3 seconds
      setTimeout(() => setActionMessage(null), 3000);
    } catch (err) {
      console.error('Error deactivating display:', err);
      setError(err instanceof Error ? err.message : 'Failed to deactivate display');
      
      // Add error notification
      addNotification({
        type: 'error',
        message: `Failed to deactivate display "${display.name}"`,
        displayId: display._id,
        displayName: display.name
      });
    }
  };

  // Format the last active time
  const formatLastActiveTime = (lastActiveDate: string) => {
    const lastActive = new Date(lastActiveDate);
    const now = new Date();
    const diffMs = now.getTime() - lastActive.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    
    if (diffMinutes < 10) {
      return 'Active now';
    } else if (diffMinutes < 60) {
      return `Last active ${diffMinutes} minutes ago`;
    } else if (diffMinutes < 1440) {
      const diffHours = Math.floor(diffMinutes / 60);
      return `Last active ${diffHours} hours ago`;
    } else {
      const diffDays = Math.floor(diffMinutes / 1440);
      return `Last active ${diffDays} days ago`;
    }
  };

  if (loading) {
    return (
      <div className="container my-4 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading display details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container my-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <button 
          className="btn btn-primary" 
          onClick={() => navigate('/my-displays')}
        >
          Back to Displays
        </button>
      </div>
    );
  }

  if (!display) {
    return (
      <div className="container my-4">
        <div className="alert alert-warning" role="alert">
          Display not found
        </div>
        <button 
          className="btn btn-primary" 
          onClick={() => navigate('/my-displays')}
        >
          Back to Displays
        </button>
      </div>
    );
  }

  return (
    <div className="container my-4">
      {actionMessage && (
        <div className={`alert alert-${actionMessage.type === 'success' ? 'success' : 'danger'}`} role="alert">
          {actionMessage.text}
        </div>
      )}
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{display.name}</h1>
        <div>
          <Link to="/my-displays" className="btn btn-outline-secondary me-2">
            Back to List
          </Link>
          
          {!isEditing && (
            <button 
              className="btn btn-primary" 
              onClick={() => setIsEditing(true)}
            >
              Edit Display
            </button>
          )}
        </div>
      </div>
      
      {isEditing ? (
        <div className="card mb-4">
          <div className="card-body">
            <DisplayForm 
              isEdit={true}
              initialData={display}
              displayId={id}
              onSubmit={handleUpdateDisplay}
            />
            <button 
              className="btn btn-outline-secondary mt-3" 
              onClick={() => setIsEditing(false)}
            >
              Cancel Editing
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  Display Information
                </div>
                <div className="card-body">
                  <dl className="row">
                    <dt className="col-sm-4">Name:</dt>
                    <dd className="col-sm-8">{display.name}</dd>
                    
                    <dt className="col-sm-4">Custom Name:</dt>
                    <dd className="col-sm-8">{display.customName || '(None)'}</dd>
                    
                    <dt className="col-sm-4">Status:</dt>
                    <dd className="col-sm-8">
                      <span className={`badge ${display.status === 'active' ? 'bg-success' : 'bg-warning'}`}>
                        {display.status === 'active' ? 'Active' : display.status === 'inactive' ? 'Inactive' : 'Pending'}
                      </span>
                    </dd>
                    
                    <dt className="col-sm-4">Location:</dt>
                    <dd className="col-sm-8">
                      {display.location?.zipcode || '(No location specified)'}
                      {display.location?.city && display.location?.state && ` (${display.location.city}, ${display.location.state})`}
                      {display.location?.label && <><br /><small>{display.location.label}</small></>}
                    </dd>
                    
                    <dt className="col-sm-4">Display ID:</dt>
                    <dd className="col-sm-8">
                      {display.client_display_id ? 
                        `...${display.client_display_id.slice(-6)}` : 
                        '(Unknown)'}
                    </dd>
                    
                    <dt className="col-sm-4">Created:</dt>
                    <dd className="col-sm-8">
                      {display.createdAt ? new Date(display.createdAt).toLocaleDateString() : '(Unknown)'}
                    </dd>
                    
                    {display.analytics?.lastActiveDate && (
                      <>
                        <dt className="col-sm-4">Last Activity:</dt>
                        <dd className="col-sm-8">
                          {formatLastActiveTime(display.analytics.lastActiveDate)}
                        </dd>
                      </>
                    )}
                  </dl>
                </div>
              </div>
            </div>
            
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  Display Analytics
                </div>
                <div className="card-body">
                  {display.analytics ? (
                    <dl className="row">
                      <dt className="col-sm-6">Total Notes Shown:</dt>
                      <dd className="col-sm-6">{display.analytics.totalNotesShown || 0}</dd>
                      
                      <dt className="col-sm-6">Total QR Scans:</dt>
                      <dd className="col-sm-6">{display.analytics.totalQrScans || 0}</dd>
                      
                      <dt className="col-sm-6">Notes Inspired:</dt>
                      <dd className="col-sm-6">{display.analytics.notesInspired || 0}</dd>
                    </dl>
                  ) : (
                    <p className="text-muted">No analytics data available</p>
                  )}
                </div>
              </div>
              
              <div className="card mt-3">
                <div className="card-header">
                  Display Actions
                </div>
                <div className="card-body">
                  {display.status === 'active' ? (
                    <>
                      <p>This display is currently active and showing notes.</p>
                      <button 
                        className="btn btn-warning" 
                        onClick={() => setShowDeactivateConfirm(true)}
                      >
                        Deactivate Display
                      </button>
                    </>
                  ) : (
                    <>
                      <p>This display is currently inactive and not showing notes.</p>
                      <button 
                        className="btn btn-success" 
                        onClick={() => setShowActivateConfirm(true)}
                      >
                        Activate Display
                      </button>
                    </>
                  )}
                  
                  {display.status === 'active' && display.static_auth_token && (
                    <div className="mt-3">
                      <p className="mb-1">Authentication Token:</p>
                      <div className="input-group">
                        <input 
                          type="password" 
                          className="form-control" 
                          value={display.static_auth_token} 
                          readOnly 
                        />
                        <button 
                          className="btn btn-outline-secondary" 
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(display.static_auth_token || '');
                            setActionMessage({ type: 'success', text: 'Token copied to clipboard' });
                            setTimeout(() => setActionMessage(null), 3000);
                          }}
                        >
                          Copy
                        </button>
                      </div>
                      <small className="text-muted">
                        This token is needed to authenticate your display. Keep it secure.
                      </small>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="card mt-3">
                <div className="card-header">
                  Display QR Code
                </div>
                <div className="card-body">
                  {display._id && (
                    <QRCodeDisplay 
                      displayId={display._id}
                      displayName={display.name}
                      size={200}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Sessions section */}
          <div className="row">
            <div className="col-12">
              <div className="card mb-4">
                <div className="card-header">
                  <ul className="nav nav-tabs card-header-tabs" id="sessions-tabs" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button 
                        className="nav-link active" 
                        id="active-sessions-tab" 
                        data-bs-toggle="tab" 
                        data-bs-target="#active-sessions" 
                        type="button" 
                        role="tab" 
                        aria-controls="active-sessions" 
                        aria-selected="true"
                      >
                        Active Sessions
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button 
                        className="nav-link" 
                        id="past-sessions-tab" 
                        data-bs-toggle="tab" 
                        data-bs-target="#past-sessions" 
                        type="button" 
                        role="tab" 
                        aria-controls="past-sessions" 
                        aria-selected="false"
                      >
                        Session History
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="card-body">
                  <div className="tab-content" id="sessions-tab-content">
                    <div 
                      className="tab-pane fade show active" 
                      id="active-sessions" 
                      role="tabpanel" 
                      aria-labelledby="active-sessions-tab"
                    >
                      {display._id && (
                        <DisplaySessionsList 
                          displayId={display._id}
                          activeOnly={true}
                          limit={5}
                          onEndSession={() => {
                            setActionMessage({ 
                              type: 'success', 
                              text: 'Session ended successfully' 
                            });
                            setTimeout(() => setActionMessage(null), 3000);
                          }}
                        />
                      )}
                    </div>
                    <div 
                      className="tab-pane fade" 
                      id="past-sessions" 
                      role="tabpanel" 
                      aria-labelledby="past-sessions-tab"
                    >
                      {display._id && (
                        <DisplaySessionsList 
                          displayId={display._id}
                          activeOnly={false}
                          limit={10}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* Confirmation Modals */}
      {showActivateConfirm && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Activation</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowActivateConfirm(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to activate this display?</p>
                <p>Once activated, it will start showing notes and be visible in the network.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowActivateConfirm(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleActivateDisplay}
                >
                  Activate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showDeactivateConfirm && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Deactivation</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeactivateConfirm(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to deactivate this display?</p>
                <p>Once deactivated, it will stop showing notes and not be visible in the network.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeactivateConfirm(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={handleDeactivateDisplay}
                >
                  Deactivate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisplayDetailPage; 